import {
  Box,
  Button,
  EmptyState,
  Icon,
  Separator,
  Text,
} from "@salestrak/ui";

import type { FilterConfig, UseFiltersResult } from "#app/lib/filters/types";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import type { Omittable } from "@salestrak/ui";
import { FilterBlock } from "./filter-block";
import { LogicSelect } from "./logic-select";

type AdvancedFiltersProps = Omittable<{
  config: FilterConfig;
  filters: UseFiltersResult;
  /** The screen's scope, when it has one. The rows offer only what the scope
   *  can still narrow — a workflow screen's stage picker lists that
   *  workflow's stages, not every workflow's. */
  scope?: IssueScope;
}>;

/**
 * First-run empty state (no blocks yet): the app's standard empty grammar —
 * icon medallion, one-line pitch, real CTA. Once the first filter exists the
 * tab reverts to the plain block builder and this never shows again.
 */
function AdvancedFiltersEmpty({ onAddFilter }: { onAddFilter: () => void }) {
  return (
    <EmptyState
      size="sm"
      media="icon"
      icon="filter"
      title="Need more advanced filters?"
      description="Combine multiple filters with conditions and logic."
      actions={
        <Button size="sm" variant="outline" onClick={onAddFilter}>
          <Icon name="plus" size="lg" />
          <Text size="sm" weight="medium">
            Filter
          </Text>
        </Button>
      }
    />
  );
}

/**
 * Advanced tab content: block-based query builder.
 *
 * Renders filter blocks with AND/OR connectors between them.
 * Each block contains N filter rows that can be independently configured.
 * The main logic operator (between blocks) is shown as a Select between
 * block 0 and block 1.
 */
export function AdvancedFilters({ config, filters, scope }: AdvancedFiltersProps) {
  const { advancedState } = filters;

  if (advancedState.blocks.length === 0) {
    return <AdvancedFiltersEmpty onAddFilter={filters.addBlock} />;
  }

  return (
    // Same 16px side frame as the simple tab's sections — the block cards land
    // on its content edge, and their 8px inner inset puts row controls on the
    // simple tab's 24px content line.
    <Box paddingX="md" paddingTop="md" paddingBottom="sm">
      <Box gap="none">
        {advancedState.blocks.map((block, blockIndex) => {
          const isFirstConnector = blockIndex === 1;
          const connector = blockIndex > 0 ? (
            <Box
              key={`connector-${blockIndex}`}
              paddingTop="md"
              paddingBottom="md"
              paddingX="sm"
            >
              <Box direction="row" align="center" gap="sm">
                {isFirstConnector ? (
                  <LogicSelect
                    value={advancedState.logic}
                    onChange={filters.setMainLogic}
                  />
                ) : (
                  <Text size="sm" variant="muted" weight="medium">
                    {advancedState.logic}
                  </Text>
                )}
                <Box fill="flex">
                  <Separator />
                </Box>
              </Box>
            </Box>
          ) : null;

          return (
            <Box key={`block-stack-${blockIndex}`} gap="none">
              {connector}
              <FilterBlock
                config={config}
                block={block}
                onAddFilter={() => filters.addFilter(blockIndex)}
                onRemoveFilter={(filterIndex) =>
                  filters.removeFilter(blockIndex, filterIndex)
                }
                onUpdateFilter={(filterIndex, update) =>
                  filters.updateFilter(blockIndex, filterIndex, update)
                }
                onSetBlockLogic={(logic) =>
                  filters.setBlockLogic(blockIndex, logic)
                }
                onRemoveBlock={() => filters.removeBlock(blockIndex)}
                scope={scope}
              />
            </Box>
          );
        })}

        {/* Add filter / add block buttons */}
        <Box paddingTop="xs">
          <Box direction="row" gap="sm" align="start">
            <Button variant="ghost" size="sm" onClick={filters.addBlock}>
              <Icon name="plus" size="lg" />
              <Text size="sm" weight="medium">Filter</Text>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
